import React, { useEffect, useState } from 'react';
import { COVERAGE_TYPE_OPTIONS, normalizeCoverageType, normalizePaymentSchedule, normalizeRefundCalculationBasis, PAYMENT_SCHEDULE_OPTIONS, REFUND_CALCULATION_BASIS_OPTIONS, validateRequiredSubcoverageTripFields } from '../../constants/tripSubcoverageCoverageType';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Spinner, Alert, Button, Form } from 'react-bootstrap';
import { FiLayers, FiArrowLeft, FiSave } from 'react-icons/fi';
import { fetchTripSubcoverageByIdRequest, updateTripSubcoverageRequest } from '../../store/actions/tripsActions';
import './AdminTripSubcoverageEdit.css';

const AdminTripSubcoverageEdit = () => {
    const { subcoverageTripId } = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { selectedSubcoverageTrip, updateTripSubcoverage } = useSelector((state) => state.trips);

    const [formData, setFormData] = useState({});
    const [formErrors, setFormErrors] = useState({});

    useEffect(() => {
        if (subcoverageTripId) {
            dispatch(fetchTripSubcoverageByIdRequest(subcoverageTripId));
        }
    }, [dispatch, subcoverageTripId]);

    useEffect(() => {
        if (selectedSubcoverageTrip.data) {
            const subcoverage = selectedSubcoverageTrip.data.subcoverage_trip || {};
            const reference = selectedSubcoverageTrip.data.reference || {};
            const isTriggerCumulatedDisabled = reference.prcp_is_cumulated === false;
            // Convertir les dates au format YYYY-MM-DD pour les inputs date
            const convertDate = (dateStr) => {
                if (!dateStr) return '';
                if (dateStr.includes('/')) {
                    const [day, month, year] = dateStr.split('/');
                    return `${year}-${month}-${day}`;
                }
                return dateStr;
            };

            // Convertir les heures au format HH:MM pour les inputs time
            const convertTime = (timeStr) => {
                if (!timeStr) return '';
                if (timeStr.includes(':')) {
                    return timeStr.substring(0, 5); // Prendre seulement HH:MM
                }
                return timeStr;
            };

            setFormData({
                trigger_type: subcoverage.trigger_type || '',
                effective_start_date: subcoverage.effective_start_date ?? '',
                effective_end_date: subcoverage.effective_end_date ?? '',
                trigger_low: subcoverage.trigger_low ?? (isTriggerCumulatedDisabled ? '0' : ''),
                unit: subcoverage.unit || '',
                trigger_sup: subcoverage.trigger_sup ?? (isTriggerCumulatedDisabled ? '0' : ''),
                forecast_timing_hour: subcoverage.forecast_timing_hour ?? '',
                forecast_timing_day: subcoverage.forecast_timing_day ?? '',
                observation_timing_hour: subcoverage.observation_timing_hour ?? '',
                observation_timing_day: subcoverage.observation_timing_day ?? '',
                siv_ratio_maintained: subcoverage.siv_ratio_maintained || '',
                siv_ratio_cancelled: subcoverage.siv_ratio_cancelled || '',
                prcp_trigger_hour_inf: subcoverage.prcp_trigger_hour_inf || '',
                trigger_hour_sup: subcoverage.trigger_hour_sup || '',
                trigger_number_hours: subcoverage.trigger_number_hours ?? '',
                orakle_price_ratio_excl_tsca: subcoverage.orakle_price_ratio_excl_tsca || '',
                orakle_price_ratio_incl_tsca: subcoverage.orakle_price_ratio_incl_tsca || '',
                sub_coverage_start_date: convertDate(subcoverage.sub_coverage_start_date),
                sub_coverage_end_date: convertDate(subcoverage.sub_coverage_end_date),
                city: subcoverage.city || '',
                country: subcoverage.country || '',
                activity_address: subcoverage.activity_address || '',
                zip_code: subcoverage.zip_code || '',
                geo_longitude: subcoverage.geo_longitude || '',
                geo_latitude: subcoverage.geo_latitude || '',
                type_id_number: subcoverage.type_id_number || '',
                voucher_share_maintain: subcoverage.voucher_share_maintain || '',
                voucher_share_cancellation: subcoverage.voucher_share_cancellation || '',
                cash_share_maintain: subcoverage.cash_share_maintain || '',
                cash_share_cancellation: subcoverage.cash_share_cancellation || '',
                day_start_time: convertTime(subcoverage.day_start_time),
                day_end_time: convertTime(subcoverage.day_end_time),
                split_voucher_cash_cancellation: subcoverage.split_voucher_cash_cancellation || false,
                split_voucher_cash_maintain: subcoverage.split_voucher_cash_maintain || false,
                lump_sum_maintain: subcoverage.lump_sum_maintain ?? '',
                lump_sum_cancellation: subcoverage.lump_sum_cancellation ?? '',
                is_lump_sum: subcoverage.is_lump_sum || false,
                maintain_data_source: subcoverage.maintain_data_source || '',
                cancellation_data_source: subcoverage.cancellation_data_source || '',
                distribution_type: subcoverage.distribution_type || 'option',
                coverage_type: subcoverage.coverage_type || '',
                payment_schedule: subcoverage.payment_schedule || 'end_of_stay',
                refund_calculation_basis: subcoverage.refund_calculation_basis || '',
                weather_parameter: subcoverage.weather_parameter || ['precipitation'],
                temperature_trigger_hour_inf: subcoverage.temperature_trigger_hour_inf ?? '',
                temperature_trigger_sup: subcoverage.temperature_trigger_sup ?? '',
                temperature_trigger_number_hours: subcoverage.temperature_trigger_number_hours ?? '',
                wind_trigger_hour_inf: subcoverage.wind_trigger_hour_inf ?? '',
                wind_trigger_sup: subcoverage.wind_trigger_sup ?? '',
                wind_trigger_number_hours: subcoverage.wind_trigger_number_hours ?? '',
                reference_id: subcoverage.reference_id || '',
                subcoverage_trip_code: subcoverage.subcoverage_trip_code || '',
                subcoverage_trip_id: subcoverage.subcoverage_trip_id || '',
                precipitation_is_triggered_forecast: subcoverage.precipitation_is_triggered_forecast ?? false,
                wind_is_triggered_forecast: subcoverage.wind_is_triggered_forecast ?? false,
            });
        }
    }, [selectedSubcoverageTrip.data]);

    useEffect(() => {
        if (updateTripSubcoverage.success) {
            navigate(`/admin/trips/subcoverages/${subcoverageTripId}`);
        }
    }, [updateTripSubcoverage.success, navigate, subcoverageTripId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
        // Effacer l'erreur pour ce champ
        if (formErrors[name]) {
            setFormErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[name];
                return newErrors;
            });
        }
    };

    const handleArrayChange = (field, value, checked) => {
        setFormData(prev => {
            const currentArray = prev[field] || [];
            if (checked) {
                return { ...prev, [field]: [...currentArray, value] };
            } else {
                return { ...prev, [field]: currentArray.filter(item => item !== value) };
            }
        });
    };

    const convertDateToApi = (dateStr) => {
        if (!dateStr) return null;
        if (dateStr.includes('-')) {
            const [year, month, day] = dateStr.split('-');
            return `${day}/${month}/${year}`;
        }
        return dateStr;
    };

    const convertTimeToApi = (timeStr) => {
        if (!timeStr) return null;
        if (timeStr.length === 5) {
            return `${timeStr}:00`;
        }
        return timeStr;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        const requiredErrors = validateRequiredSubcoverageTripFields(formData);
        setFormErrors(requiredErrors);
        if (Object.keys(requiredErrors).length > 0) {
            return;
        }

        // Préparer les données pour l'API
        const payload = {
            ...formData,
            sub_coverage_start_date: convertDateToApi(formData.sub_coverage_start_date),
            sub_coverage_end_date: convertDateToApi(formData.sub_coverage_end_date),
            day_start_time: convertTimeToApi(formData.day_start_time),
            day_end_time: convertTimeToApi(formData.day_end_time),
        };

        // Convertir les valeurs numériques
        const numberFields = [
            'forecast_timing_hour', 'forecast_timing_day', 'observation_timing_hour',
            'trigger_number_hours', 'prcp_trigger_hour_inf', 'temperature_trigger_hour_inf', 'temperature_trigger_sup',
            'temperature_trigger_number_hours', 'wind_trigger_hour_inf', 'wind_trigger_sup',
            'wind_trigger_number_hours', 'lump_sum_maintain', 'lump_sum_cancellation'
        ];

        numberFields.forEach(field => {
            if (payload[field] !== '' && payload[field] !== null && payload[field] !== undefined) {
                const normalizedValue = typeof payload[field] === 'string'
                    ? payload[field].replace(',', '.')
                    : payload[field];
                const parsedValue = Number(normalizedValue);
                payload[field] = Number.isNaN(parsedValue) ? null : parsedValue;
            } else if (payload[field] === '') {
                payload[field] = null;
            }
        });

        payload.coverage_type = normalizeCoverageType(payload.coverage_type);
        payload.refund_calculation_basis = normalizeRefundCalculationBasis(payload.refund_calculation_basis);
        payload.payment_schedule = normalizePaymentSchedule(payload.payment_schedule);

        dispatch(updateTripSubcoverageRequest(subcoverageTripId, payload));
    };

    const loading = selectedSubcoverageTrip.loading;
    const error = selectedSubcoverageTrip.error;
    const data = selectedSubcoverageTrip.data || {};
    const subcoverage = data.subcoverage_trip || {};

    return (
        <div className="dashboard-page admin-trip-subcoverage-edit">
            <Card className="mb-3">
                <Card.Body className="d-flex justify-content-between align-items-center">
                    <div>
                        <h2 className="page-title">
                            <FiLayers className="me-2" />
                            Modifier la sous-couverture séjour
                        </h2>
                        <p className="text-muted mb-0">
                            Modifiez les informations de la sous-couverture associée au séjour.
                        </p>
                    </div>
                    <Button variant="outline-secondary" onClick={() => navigate(-1)}>
                        <FiArrowLeft className="me-1" /> Retour
                    </Button>
                </Card.Body>
            </Card>

            {loading && (
                <div className="text-center my-5">
                    <Spinner animation="border" variant="primary" />
                </div>
            )}

            {!loading && error && (
                <Alert variant="danger">{error}</Alert>
            )}

            {updateTripSubcoverage.error && (
                <Alert variant="danger">{updateTripSubcoverage.error}</Alert>
            )}

            {!loading && !error && data && (
                <Form onSubmit={handleSubmit}>
                    {/* Section Informations générales */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Informations générales</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Type de déclencheur *</Form.Label>
                                        <Form.Select
                                            name="trigger_type"
                                            value={formData.trigger_type || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={updateTripSubcoverage.loading}
                                        >
                                            <option value="">Sélectionner...</option>
                                            <option value="forecast">Forecast</option>
                                            <option value="observation">Observation</option>
                                            <option value="forecast and observation">Forecast and Observation</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Unité *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="unit"
                                            value={formData.unit || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={updateTripSubcoverage.loading}
                                            placeholder="Ex: mm, °C, km/h"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Trigger inférieur</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="trigger_low"
                                            value={formData.trigger_low ?? ''}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Heures de déclenchement</Form.Label>
                                        <Form.Control
                                            type="number"
                                            name="trigger_number_hours"
                                            value={formData.trigger_number_hours ?? ''}
                                            onChange={handleChange}
                                            min="0"
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Ratio prix Orakle (excl. TSCA) *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="orakle_price_ratio_excl_tsca"
                                            value={formData.orakle_price_ratio_excl_tsca || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Ratio prix Orakle (incl. TSCA)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="orakle_price_ratio_incl_tsca"
                                            value={formData.orakle_price_ratio_incl_tsca || ''}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Section Période et horaires */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Période et horaires</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date de début *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="sub_coverage_start_date"
                                            value={formData.sub_coverage_start_date || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date de fin *</Form.Label>
                                        <Form.Control
                                            type="date"
                                            name="sub_coverage_end_date"
                                            value={formData.sub_coverage_end_date || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Heure de début journée *</Form.Label>
                                        <Form.Control
                                            type="time"
                                            name="day_start_time"
                                            value={formData.day_start_time || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Heure de fin journée *</Form.Label>
                                        <Form.Control
                                            type="time"
                                            name="day_end_time"
                                            value={formData.day_end_time || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date effective début</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="effective_start_date"
                                            value={formData.effective_start_date ?? ''}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                            placeholder="Ex: +1"
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Date effective fin</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="effective_end_date"
                                            value={formData.effective_end_date ?? ''}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                            placeholder="Ex: -1"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Section Timing */}
                    {formData.trigger_type && (formData.trigger_type === 'forecast' || formData.trigger_type === 'forecast and observation') && (
                        <Card className="mb-3">
                            <Card.Header>
                                <h5 className="mb-0">Timing Forecast</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Heure de timing forecast</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="forecast_timing_hour"
                                                value={formData.forecast_timing_hour ?? ''}
                                                onChange={handleChange}
                                                min="0"
                                                max="23"
                                                disabled={updateTripSubcoverage.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Jour de timing forecast</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="forecast_timing_day"
                                                value={formData.forecast_timing_day ?? ''}
                                                onChange={handleChange}
                                                disabled={updateTripSubcoverage.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {formData.trigger_type && (formData.trigger_type === 'observation' || formData.trigger_type === 'forecast and observation') && (
                        <Card className="mb-3">
                            <Card.Header>
                                <h5 className="mb-0">Timing Observation</h5>
                            </Card.Header>
                            <Card.Body>
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Heure de timing observation</Form.Label>
                                            <Form.Control
                                                type="number"
                                                name="observation_timing_hour"
                                                value={formData.observation_timing_hour ?? ''}
                                                onChange={handleChange}
                                                min="0"
                                                max="23"
                                                disabled={updateTripSubcoverage.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Jour de timing observation</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="observation_timing_day"
                                                value={formData.observation_timing_day ?? ''}
                                                onChange={handleChange}
                                                disabled={updateTripSubcoverage.loading}
                                                placeholder="Ex: +1"
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Section Localisation */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Localisation</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Ville *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="city"
                                            value={formData.city || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Pays *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="country"
                                            value={formData.country || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Adresse de l'activité *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="activity_address"
                                            value={formData.activity_address || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Code postal *</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="zip_code"
                                            value={formData.zip_code || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Longitude</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="geo_longitude"
                                            value={formData.geo_longitude || ''}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={3}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Latitude</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="geo_latitude"
                                            value={formData.geo_latitude || ''}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Section Indemnisation */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Indemnisation</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            name="is_lump_sum"
                                            label="Forfait (lump sum)"
                                            checked={formData.is_lump_sum || false}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {formData.is_lump_sum ? (
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Forfait maintien</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="lump_sum_maintain"
                                                    value={formData.lump_sum_maintain ?? ''}
                                                    onChange={handleChange}
                                                    disabled={updateTripSubcoverage.loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Forfait annulation</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="lump_sum_cancellation"
                                                    value={formData.lump_sum_cancellation ?? ''}
                                                    onChange={handleChange}
                                                    disabled={updateTripSubcoverage.loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Part cash maintien *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="cash_share_maintain"
                                                    value={formData.cash_share_maintain || ''}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={updateTripSubcoverage.loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Part cash annulation *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="cash_share_cancellation"
                                                    value={formData.cash_share_cancellation || ''}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={updateTripSubcoverage.loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </>
                            ) : (
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Ratio SIV maintenu *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="siv_ratio_maintained"
                                                    value={formData.siv_ratio_maintained || ''}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={updateTripSubcoverage.loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Ratio SIV annulé *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="siv_ratio_cancelled"
                                                    value={formData.siv_ratio_cancelled || ''}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={updateTripSubcoverage.loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Part cash maintien *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="cash_share_maintain"
                                                    value={formData.cash_share_maintain || ''}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={updateTripSubcoverage.loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Part cash annulation *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="cash_share_cancellation"
                                                    value={formData.cash_share_cancellation || ''}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={updateTripSubcoverage.loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            name="split_voucher_cash_maintain"
                                            label="Diviser voucher/cash maintien"
                                            checked={formData.split_voucher_cash_maintain || false}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            name="split_voucher_cash_cancellation"
                                            label="Diviser voucher/cash annulation"
                                            checked={formData.split_voucher_cash_cancellation || false}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>

                            {formData.split_voucher_cash_maintain && (
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Part voucher maintien</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="voucher_share_maintain"
                                                value={formData.voucher_share_maintain || ''}
                                                onChange={handleChange}
                                                disabled={updateTripSubcoverage.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            )}

                            {formData.split_voucher_cash_cancellation && (
                                <Row>
                                    <Col md={6}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Part voucher annulation</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="voucher_share_cancellation"
                                                value={formData.voucher_share_cancellation || ''}
                                                onChange={handleChange}
                                                disabled={updateTripSubcoverage.loading}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Section Sources de données */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Sources de données</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Source données maintien *</Form.Label>
                                        <Form.Select
                                            name="maintain_data_source"
                                            value={formData.maintain_data_source || ''}
                                            onChange={handleChange}
                                            required
                                            disabled={updateTripSubcoverage.loading}
                                        >
                                            <option value="">Sélectionner...</option>
                                            <option value="MF">MF</option>
                                            <option value="ECMWF">ECMWF</option>
                                            <option value="Meteostat">Meteostat</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Source données annulation</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="cancellation_data_source"
                                            value={formData.cancellation_data_source || ''}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                            placeholder="Par défaut: ECMWF"
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Section Paramètres météo */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Paramètres météorologiques</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={12}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Paramètres météo *</Form.Label>
                                        <div>
                                            <Form.Check
                                                type="checkbox"
                                                label="Précipitation"
                                                checked={formData.weather_parameter?.includes('precipitation') || false}
                                                onChange={(e) => handleArrayChange('weather_parameter', 'precipitation', e.target.checked)}
                                                disabled={updateTripSubcoverage.loading}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                label="Température"
                                                checked={formData.weather_parameter?.includes('temperature') || false}
                                                onChange={(e) => handleArrayChange('weather_parameter', 'temperature', e.target.checked)}
                                                disabled={updateTripSubcoverage.loading}
                                            />
                                            <Form.Check
                                                type="checkbox"
                                                label="Vent"
                                                checked={formData.weather_parameter?.includes('wind') || false}
                                                onChange={(e) => handleArrayChange('weather_parameter', 'wind', e.target.checked)}
                                                disabled={updateTripSubcoverage.loading}
                                            />
                                        </div>
                                        {formErrors.weather_parameter && (
                                            <div className="text-danger small mt-1">{formErrors.weather_parameter}</div>
                                        )}
                                    </Form.Group>
                                </Col>
                            </Row>

                            {formData.weather_parameter?.includes('temperature') && (
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Température — seuil horaire inférieur *</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    name="temperature_trigger_hour_inf"
                                                    value={formData.temperature_trigger_hour_inf ?? ''}
                                                    onChange={handleChange}
                                                    inputMode="decimal"
                                                    placeholder='Ex: 12,0'
                                                    required
                                                    disabled={updateTripSubcoverage.loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Nombre d'heures trigger température *</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    name="temperature_trigger_number_hours"
                                                    value={formData.temperature_trigger_number_hours ?? ''}
                                                    onChange={handleChange}
                                                    min="0"
                                                    required
                                                    disabled={updateTripSubcoverage.loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            {formData.weather_parameter?.includes('wind') && (
                                <>
                                    <Row>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Vent — seuil horaire inférieur *</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    name="wind_trigger_hour_inf"
                                                    value={formData.wind_trigger_hour_inf ?? ''}
                                                    onChange={handleChange}
                                                    step="0.1"
                                                    required
                                                    disabled={updateTripSubcoverage.loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Nombre d'heures trigger vent *</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    name="wind_trigger_number_hours"
                                                    value={formData.wind_trigger_number_hours ?? ''}
                                                    onChange={handleChange}
                                                    min="0"
                                                    required
                                                    disabled={updateTripSubcoverage.loading}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </Row>
                                </>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Section Autres paramètres */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h5 className="mb-0">Autres paramètres</h5>
                        </Card.Header>
                        <Card.Body>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Type de couverture *</Form.Label>
                                        <Form.Select
                                            name="coverage_type"
                                            value={formData.coverage_type || ''}
                                            onChange={handleChange}
                                            required
                                            isInvalid={!!formErrors.coverage_type}
                                            disabled={updateTripSubcoverage.loading}
                                        >
                                            <option value="">— Sélectionner —</option>
                                            {COVERAGE_TYPE_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.coverage_type}
                                        </Form.Control.Feedback>
                                        <Form.Text className="text-muted">
                                            Valeurs acceptées : maintain, cancellation, maintain_and_cancellation
                                        </Form.Text>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Type de distribution</Form.Label>
                                        <Form.Select
                                            name="distribution_type"
                                            value={formData.distribution_type || 'option'}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        >
                                            <option value="inclusion">Inclusion</option>
                                            <option value="option">Option</option>
                                            <option value="mixed">Mixed</option>
                                            <option value="b2c">B2C</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Plan de paiement *</Form.Label>
                                        <Form.Select
                                            name="payment_schedule"
                                            value={formData.payment_schedule || 'end_of_stay'}
                                            onChange={handleChange}
                                            required
                                            isInvalid={!!formErrors.payment_schedule}
                                            disabled={updateTripSubcoverage.loading}
                                        >
                                            {PAYMENT_SCHEDULE_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.payment_schedule}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Base de calcul du remboursement *</Form.Label>
                                        <Form.Select
                                            name="refund_calculation_basis"
                                            value={formData.refund_calculation_basis || ''}
                                            onChange={handleChange}
                                            required
                                            isInvalid={!!formErrors.refund_calculation_basis}
                                            disabled={updateTripSubcoverage.loading}
                                        >
                                            <option value="">— Sélectionner —</option>
                                            {REFUND_CALCULATION_BASIS_OPTIONS.map((option) => (
                                                <option key={option.value} value={option.value}>
                                                    {option.label}
                                                </option>
                                            ))}
                                        </Form.Select>
                                        <Form.Control.Feedback type="invalid">
                                            {formErrors.refund_calculation_basis}
                                        </Form.Control.Feedback>
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Reference ID</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="reference_id"
                                            value={formData.reference_id || ''}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Subcoverage Trip Code</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="subcoverage_trip_code"
                                            value={formData.subcoverage_trip_code || ''}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Subcoverage Trip ID</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="subcoverage_trip_id"
                                            value={formData.subcoverage_trip_id || ''}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Type ID Number</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="type_id_number"
                                            value={formData.type_id_number || ''}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Précipitations — seuil horaire inférieur (prcp_trigger_hour_inf)</Form.Label>
                                        <Form.Control
                                            type="text"
                                            name="prcp_trigger_hour_inf"
                                            value={formData.prcp_trigger_hour_inf || ''}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                            <Row>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            name="precipitation_is_triggered_forecast"
                                            label="Précipitation déclenchée sur forecast"
                                            checked={formData.precipitation_is_triggered_forecast || false}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                                <Col md={6}>
                                    <Form.Group className="mb-3">
                                        <Form.Check
                                            type="checkbox"
                                            name="wind_is_triggered_forecast"
                                            label="Vent déclenché sur forecast"
                                            checked={formData.wind_is_triggered_forecast || false}
                                            onChange={handleChange}
                                            disabled={updateTripSubcoverage.loading}
                                        />
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Card.Body>
                    </Card>

                    {/* Boutons d'action */}
                    <Card className="mb-3">
                        <Card.Body className="d-flex justify-content-end gap-2">
                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate(-1)}
                                disabled={updateTripSubcoverage.loading}
                            >
                                Annuler
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={updateTripSubcoverage.loading}
                            >
                                {updateTripSubcoverage.loading ? (
                                    <>
                                        <Spinner size="sm" className="me-2" />
                                        Enregistrement...
                                    </>
                                ) : (
                                    <>
                                        <FiSave className="me-2" />
                                        Enregistrer
                                    </>
                                )}
                            </Button>
                        </Card.Body>
                    </Card>
                </Form>
            )}
        </div>
    );
};

export default AdminTripSubcoverageEdit;
